/* ==========================================================================
   Custom mobile sticky add-to-cart bar — behaviour
   Pairs with snippets/sticky-atc-bar-mobile.liquid + assets/sticky-atc-bar-mobile.css

   Self-initialising, dependency-free, no build step. Does NOT touch theme.js /
   theme.min.js. Loaded via the snippet's own asset_url tag (PDP-only).

   Integration notes (verified against assets/theme.js):

   - Cart/drawer events travel on an INTERNAL closure event bus (theme.js:2387).
     Sanctioned cross-script hooks: document event "apps:product-added-to-cart"
     (quick-cart refresh, theme.js:8730; header count, theme.js:8210) and the header
     cart icon [data-js-cart-icon] (theme.js:8214). So: add -> dispatch
     apps:product-added-to-cart -> after ~300ms click the cart icon (guarded).

   - --sticky-atc-bar-height only lifts back-to-top / promo tabs (theme.css:16386-16455).
     Footer overlap is prevented by a footer IntersectionObserver (as theme.js:6138).
     Var is set ONLY on mobile so the native desktop bar keeps its own value.

   - Option -> variant resolution is positional (theme.js:3848).

   - Money uses window.theme.moneyFormat via a formatMoney clone (theme.js:4417).

   - Analytics: GA4 add_to_cart dataLayer.push, guarded on window.dataLayer.

   - Variant JSON is trimmed to {id, options, available, price, compare_at_price}
     (compare_at_price 0 = none). Sale strike AND the discount label (percent off)
     are computed per selected variant + quantity, so they stay correct when the
     shopper switches flavour / packaging in the drawer.

   - Checkout button: adds current variant + quantity via /cart/add.js, then
     redirects to the localized checkout. Shares the in-flight guard with add.
   ========================================================================== */
(function () {
  'use strict';

  var BAR_SELECTOR = '[data-sticky-atc-mobile]';
  var BUY_BUTTONS_SELECTOR = '.product-form__item--submit';
  var FOOTER_SELECTOR = '.footer, [data-section-type="footer"], footer';
  var OVERLAY_ATTR = 'data-fluorescent-overlay-open';
  var HEIGHT_VAR = '--sticky-atc-bar-height';
  var MOBILE_MQ = '(max-width: 959px)';
  var SUCCESS_MS = 2000;
  var ERROR_MS = 3000;
  var OPEN_DELAY_MS = 300;

  /* ---- Money (clone of theme.js formatMoney) --------------------------- */

  var moneyFormat = (window.theme && window.theme.moneyFormat) || '${{amount}}';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = precision == null ? 2 : precision;
    thousands = thousands == null ? ',' : thousands;
    decimal = decimal == null ? '.' : decimal;
    if (isNaN(number) || number == null) return '0';
    number = (number / 100.0).toFixed(precision);
    var parts = number.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    var cents = parts[1] ? decimal + parts[1] : '';
    return dollars + cents;
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || moneyFormat;
    var match = formatString.match(placeholderRegex);
    switch (match ? match[1] : 'amount') {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      case 'amount_no_decimals_with_space_separator': value = formatWithDelimiters(cents, 0, ' ', ','); break;
      case 'amount_with_space_separator': value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_with_period_and_space_separator': value = formatWithDelimiters(cents, 2, ' ', '.'); break;
      default: value = formatWithDelimiters(cents, 2); break;
    }
    return formatString.replace(placeholderRegex, value);
  }

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  /* ---- Controller ------------------------------------------------------ */

  function StickyAtcMobile(root) {
    this.root = root;
    this.bar = root.querySelector('.sticky-atc-bar-mobile__bar');
    this.panel = root.querySelector('[data-sticky-panel]');
    this.expandToggle = root.querySelector('[data-sticky-expand-toggle]');
    this.addButton = root.querySelector('[data-sticky-add]');
    this.addText = root.querySelector('[data-sticky-add-text]');
    this.priceEl = root.querySelector('[data-sticky-price]');
    this.compareEl = root.querySelector('[data-sticky-compare]');
    this.discountEl = root.querySelector('[data-sticky-discount]');
    this.discountTemplate = this.discountEl ? (this.discountEl.getAttribute('data-discount-template') || '') : '';
    this.titleEl = root.querySelector('.sticky-atc-bar-mobile__title');
    this.optionSummary = root.querySelector('[data-sticky-option-summary]');
    this.qtyInput = root.querySelector('[data-sticky-qty-input]');
    this.qtyDecrease = root.querySelector('[data-sticky-qty-decrease]');
    this.qtyIncrease = root.querySelector('[data-sticky-qty-increase]');
    this.checkoutButton = root.querySelector('[data-sticky-checkout]');
    this.checkoutText = root.querySelector('.sticky-atc-bar-mobile__checkout-text');
    this.optionGroups = toArray(root.querySelectorAll('[data-sticky-option]'));
    this.valueButtons = toArray(root.querySelectorAll('[data-sticky-option-value]'));

    this.quantityMax = parseInt(root.getAttribute('data-quantity-max'), 10) || 99;
    this.quantity = 1;
    this.inFlight = false;
    this.visible = false;
    this.buyButtonsVisible = true;
    this.footerVisible = false;
    this.overlayOpen = document.body.getAttribute(OVERLAY_ATTR) === 'true';
    this.timers = { reset: null, open: null };

    // Trimmed variant data: [{ id, options:[...], available, price, compare_at_price }]
    var json = root.querySelector('[data-sticky-atc-variants]');
    try { this.variants = JSON.parse(json.textContent); } catch (e) { this.variants = []; }

    // Initial selection (server pre-selects a value per option).
    this.optionCount = this.optionGroups.length;
    if (this.optionCount === 0) {
      this.currentVariant = this.variants[0] || null;
      this.selected = this.currentVariant ? this.currentVariant.options.slice() : [];
    } else {
      this.selected = new Array(this.optionCount);
      this.optionGroups.forEach(function (group) {
        var pos = this.positionOf(group);
        var sel = group.querySelector('.is-selected') || group.querySelector('[aria-pressed="true"]');
        if (sel) this.selected[pos - 1] = sel.getAttribute('data-option-value');
      }, this);
      this.currentVariant = this.matchVariant(this.selected);
    }

    this.initialVariantId = this.currentVariant ? this.currentVariant.id : null;
    // Server-rendered compare-at price of the initial variant; used only as a fallback
    // when the variant JSON is unavailable.
    this.compareUnit = this.compareEl ? parseInt(this.compareEl.getAttribute('data-compare-unit'), 10) : NaN;

    this.bindEvents();
    this.initPurchase(); // CELEBRATEVITAMINS CUSTOM — one-time / subscribe in the drawer
    this.setupVisibility();
    this.updateAvailability();
    this.updateSummary();
    this.updatePrice();
    this.updateAtcState();
  }

  StickyAtcMobile.prototype.positionOf = function (el) {
    var attr = el.getAttribute('data-option-position') || el.getAttribute('data-option-index') || '';
    return parseInt(String(attr).replace('option', ''), 10);
  };

  /* ---- Variant matching (positional, theme.js:3848) -------------------- */

  StickyAtcMobile.prototype.matchVariant = function (selected) {
    if (!selected) return null;
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      var ok = true;
      for (var j = 0; j < selected.length; j++) {
        if (v.options[j] !== selected[j]) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  };

  // A value at option position `pos` (1-based) is available if some AVAILABLE
  // variant carries it while matching the current selection for every other option.
  StickyAtcMobile.prototype.valueAvailable = function (pos, value) {
    for (var i = 0; i < this.variants.length; i++) {
      var v = this.variants[i];
      if (!v.available) continue;
      if (v.options[pos - 1] !== value) continue;
      var ok = true;
      for (var p = 0; p < this.selected.length; p++) {
        if (p + 1 === pos) continue;
        if (this.selected[p] != null && v.options[p] !== this.selected[p]) { ok = false; break; }
      }
      if (ok) return true;
    }
    return false;
  };

  /* ---- Events ---------------------------------------------------------- */

  StickyAtcMobile.prototype.bindEvents = function () {
    var self = this;

    if (this.expandToggle) {
      this.expandToggle.addEventListener('click', function () { self.toggleExpanded(); });
    }

    this.valueButtons.forEach(function (btn) {
      btn.addEventListener('click', function () { self.onSelectValue(btn); });
    });

    if (this.qtyDecrease) {
      this.qtyDecrease.addEventListener('click', function () { self.setQuantity(self.quantity - 1); });
    }
    if (this.qtyIncrease) {
      this.qtyIncrease.addEventListener('click', function () { self.setQuantity(self.quantity + 1); });
    }
    if (this.qtyInput) {
      this.qtyInput.addEventListener('change', function () { self.setQuantity(self.qtyInput.value); });
    }

    if (this.addButton) {
      this.addButton.addEventListener('click', function () { self.onAdd(); });
    }
    if (this.checkoutButton) {
      this.checkoutButton.addEventListener('click', function (e) {
        e.preventDefault(); // intercept the no-JS fallback link
        e.stopPropagation(); // theme navigation must not override add-then-checkout
        self.onCheckout();
      });
    }
  };

  StickyAtcMobile.prototype.setExpanded = function (expanded) {
    if (expanded) this.root.classList.add('is-expanded');
    else this.root.classList.remove('is-expanded');
    if (this.expandToggle) this.expandToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    if (this.panel) this.panel.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    if (this.visible) this.setHeightVar();
  };

  StickyAtcMobile.prototype.toggleExpanded = function () {
    this.setExpanded(!this.root.classList.contains('is-expanded'));
  };

  StickyAtcMobile.prototype.isExpanded = function () {
    return this.root.classList.contains('is-expanded');
  };

  StickyAtcMobile.prototype.onSelectValue = function (button) {
    if (button.getAttribute('aria-disabled') === 'true') return;
    var pos = parseInt(button.getAttribute('data-option-index'), 10);
    var value = button.getAttribute('data-option-value');
    if (!pos) return;

    this.selected[pos - 1] = value;

    // Reflect selection within this option group only.
    var group = button.closest('[data-sticky-option]');
    if (group) {
      toArray(group.querySelectorAll('[data-sticky-option-value]')).forEach(function (chip) {
        var isSel = chip === button;
        chip.classList.toggle('is-selected', isSel);
        chip.setAttribute('aria-pressed', isSel ? 'true' : 'false');
      });
    }

    this.currentVariant = this.matchVariant(this.selected);
    if (this.currentVariant) {
      this.root.setAttribute('data-current-variant-id', this.currentVariant.id);
    }

    this.updateAvailability();
    this.updateSummary();
    this.updatePrice();
    this.updateAtcState();
  };

  StickyAtcMobile.prototype.setQuantity = function (q) {
    q = parseInt(q, 10);
    if (isNaN(q) || q < 1) q = 1;
    if (q > this.quantityMax) q = this.quantityMax;
    this.quantity = q;
    if (this.qtyInput) this.qtyInput.value = q;
    this.updatePrice();
  };

  /* ---- Rendering ------------------------------------------------------- */

  StickyAtcMobile.prototype.updateAvailability = function () {
    this.valueButtons.forEach(function (btn) {
      var pos = parseInt(btn.getAttribute('data-option-index'), 10);
      var value = btn.getAttribute('data-option-value');
      var selected = this.selected[pos - 1] === value;
      var available = selected || this.valueAvailable(pos, value);
      btn.classList.toggle('is-unavailable', !available);
      if (available) {
        btn.removeAttribute('aria-disabled');
      } else {
        btn.setAttribute('aria-disabled', 'true');
      }
    }, this);
  };

  StickyAtcMobile.prototype.updateSummary = function () {
    if (this.optionSummary) {
      this.optionSummary.textContent = this.selected.filter(Boolean).join(', ');
    }
  };

  /* ---- CELEBRATEVITAMINS CUSTOM — purchase type (one-time / subscribe) --
     Mirrors the PDP subscription widget (assets/celebrate-subscription.js) through
     window.CelebrateSubscription. One shared choice: changing it here changes the
     widget on the page and vice versa. Without the widget nothing is rendered and
     the bar behaves exactly as before (one-time only). */

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function subApi() {
    var api = window.CelebrateSubscription;
    return api && typeof api.resolve === 'function' && api.widget() ? api : null;
  }

  StickyAtcMobile.prototype.initPurchase = function () {
    var self = this;
    this.purchase = null;
    this.buildPurchase();
    // The widget may boot after this bar; it announces every state change.
    document.addEventListener('cv-subscription:state', function () {
      self.buildPurchase();
      self.updatePrice();
    });
  };

  StickyAtcMobile.prototype.buildPurchase = function () {
    if (this.purchase || !this.panel) return;
    var api = subApi();
    var L = api && api.labels();
    if (!L) return;

    var wrap = document.createElement('div');
    wrap.className = 'cv-sticky';
    wrap.setAttribute('data-sticky-purchase', '');
    var accent = '';
    try { accent = getComputedStyle(api.widget()).getPropertyValue('--cv-accent').trim(); } catch (e) { /* noop */ }
    if (accent) wrap.style.setProperty('--cv-accent', accent);

    wrap.innerHTML =
      '<div class="cv-sticky__modes" role="group" aria-label="' + esc(L.legend) + '">' +
        '<button type="button" class="cv-sticky__mode" data-cv-sticky-mode="onetime" aria-pressed="false">' +
          '<span class="cv-sticky__mode-title">' + esc(L.onetime) + '</span>' +
          '<span class="cv-sticky__mode-price" data-cv-sticky-onetime></span>' +
        '</button>' +
        '<button type="button" class="cv-sticky__mode" data-cv-sticky-mode="subscribe" aria-pressed="false">' +
          '<span class="cv-sticky__mode-title">' + esc(L.subscribe) +
            ' <span class="cv-sticky__badge" data-cv-sticky-badge></span></span>' +
          '<span class="cv-sticky__mode-price"><span data-cv-sticky-freq></span> · <span data-cv-sticky-sub></span></span>' +
        '</button>' +
      '</div>' +
      '<div class="cv-sticky__terms sticky-atc-bar-mobile__option-values" role="group" aria-label="' + esc(L.term) + '" data-cv-sticky-terms>' +
        '<button type="button" class="sticky-atc-bar-mobile__option-value fs-body-60" data-cv-sticky-commit="flex" aria-pressed="false">' +
          esc(L.flexible) + '&nbsp;<span class="cv-sticky__pct" data-cv-sticky-pct="flex"></span></button>' +
        '<button type="button" class="sticky-atc-bar-mobile__option-value fs-body-60" data-cv-sticky-commit="commit" aria-pressed="false">' +
          esc(L.commit) + '&nbsp;<span class="cv-sticky__pct" data-cv-sticky-pct="commit"></span></button>' +
      '</div>';

    // Below flavour / size / quantity, like on the product page.
    this.panel.appendChild(wrap);
    this.purchase = wrap;
    this.subLabels = L;

    var self = this;
    toArray(wrap.querySelectorAll('[data-cv-sticky-mode]')).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var r = self.resolvePurchase();
        var a = subApi();
        if (!a) return;
        a.set(r ? { mode: btn.getAttribute('data-cv-sticky-mode'), freq: r.freq, commit: r.commit }
                : { mode: btn.getAttribute('data-cv-sticky-mode') });
      });
    });
    toArray(wrap.querySelectorAll('[data-cv-sticky-commit]')).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var r = self.resolvePurchase();
        var a = subApi();
        if (!a || !r) return;
        a.set({ mode: 'subscribe', freq: r.freq, commit: btn.getAttribute('data-cv-sticky-commit') });
      });
    });
  };

  // Shared choice resolved for the variant selected in THIS bar.
  StickyAtcMobile.prototype.resolvePurchase = function () {
    var api = subApi();
    var v = this.currentVariant;
    return api && v ? api.resolve(v.id) : null;
  };

  StickyAtcMobile.prototype.renderPurchase = function (r) {
    var wrap = this.purchase;
    if (!wrap) return;
    if (!r || !r.hasAny) { wrap.hidden = true; return; }
    wrap.hidden = false;
    var L = this.subLabels;
    var pct = function (p) { return (L.pct || '-__V__%').replace('__V__', p); };
    var q = this.quantity;

    toArray(wrap.querySelectorAll('[data-cv-sticky-mode]')).forEach(function (btn) {
      var m = btn.getAttribute('data-cv-sticky-mode');
      var on = m === r.mode;
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (m === 'onetime') btn.hidden = !!r.requiresPlan;
    });

    var oneEl = wrap.querySelector('[data-cv-sticky-onetime]');
    if (oneEl) oneEl.textContent = formatMoney(r.variant.cents * q);
    var subEl = wrap.querySelector('[data-cv-sticky-sub]');
    var badge = wrap.querySelector('[data-cv-sticky-badge]');
    var freqEl = wrap.querySelector('[data-cv-sticky-freq]');
    if (r.plan) {
      if (subEl) subEl.textContent = formatMoney(r.plan.cents * q);
      if (badge) badge.textContent = pct(r.plan.pct);
      if (freqEl) freqEl.textContent = r.freq === 'm1' ? L.every_month : L.every_3_months;
    }

    var terms = wrap.querySelector('[data-cv-sticky-terms]');
    var hasCommit = !!r.plans[r.freq + '_commit'];
    if (terms) terms.hidden = !(r.mode === 'subscribe' && hasCommit);
    toArray(wrap.querySelectorAll('[data-cv-sticky-commit]')).forEach(function (btn) {
      var c = btn.getAttribute('data-cv-sticky-commit');
      var p = r.plans[r.freq + '_' + c];
      var on = r.mode === 'subscribe' && c === r.commit;
      btn.hidden = !p;
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      var pe = btn.querySelector('[data-cv-sticky-pct]');
      if (pe) pe.textContent = p ? pct(p.pct) : '';
    });
    if (this.visible) this.setHeightVar();
  };

  StickyAtcMobile.prototype.updatePrice = function () {
    var v = this.currentVariant;
    var unit = v ? v.price : parseInt(this.priceEl.getAttribute('data-unit-price'), 10);
    if (isNaN(unit)) unit = 0;

    // CELEBRATEVITAMINS CUSTOM — subscription price when "subscribe" is chosen.
    var r = this.resolvePurchase();
    this.renderPurchase(r);
    var oneTimeUnit = unit;
    var onSub = !!(r && r.mode === 'subscribe' && r.plan && typeof r.plan.cents === 'number');
    if (onSub) unit = r.plan.cents;
    this.effectiveUnit = unit;

    if (this.priceEl) this.priceEl.textContent = formatMoney(unit * this.quantity);

    // Sale strike + discount label per selected variant: compare_at_price ships in the
    // variant JSON (0 = no compare-at price). Falls back to the server-rendered value
    // only for the initial variant when the JSON carries no compare_at_price.
    var compareUnit = NaN;
    if (v && typeof v.compare_at_price === 'number') {
      compareUnit = v.compare_at_price;
    } else if (v && this.initialVariantId && v.id === this.initialVariantId) {
      compareUnit = this.compareUnit;
    }
    // On subscription, strike the one-time price (or the compare-at price if higher).
    if (onSub) compareUnit = Math.max(isNaN(compareUnit) ? 0 : compareUnit, oneTimeUnit);
    var showCompare = !isNaN(compareUnit) && compareUnit > unit;

    if (this.compareEl) {
      this.compareEl.textContent = showCompare ? formatMoney(compareUnit * this.quantity) : '';
    }
    if (this.priceEl) this.priceEl.classList.toggle('sticky-atc-bar-mobile__total--sale', showCompare);
    if (this.discountEl) {
      if (showCompare) {
        var pct = Math.round(((compareUnit - unit) / compareUnit) * 100);
        this.discountEl.textContent = this.discountTemplate.replace('__V__', pct);
      } else {
        this.discountEl.textContent = '';
      }
    }
  };

  StickyAtcMobile.prototype.updateAtcState = function () {
    if (!this.addButton) return;
    var v = this.currentVariant;
    var available = !!(v && v.available);

    // Checkout mirrors availability at all times (even during the add button's
    // transient states) — you can't check out a sold-out combination.
    if (this.checkoutButton && !this.checkoutButton.classList.contains('loading')) {
      this.checkoutButton.classList.toggle('is-disabled', !available);
    }

    // Don't fight the add button's loading/success/error transient states.
    if (this.inFlight ||
        this.addButton.classList.contains('is-added') ||
        this.addButton.classList.contains('is-error')) {
      return;
    }
    var label;
    if (available) {
      label = this.addButton.getAttribute('data-lang-add');
    } else if (v) {
      label = this.addButton.getAttribute('data-lang-sold-out');
    } else {
      label = this.addButton.getAttribute('data-lang-unavailable');
    }
    if (label && this.addText) this.addText.textContent = label;
    if (label) this.addButton.setAttribute('aria-label', label);

    this.addButton.disabled = !available;
    if (available) {
      this.addButton.removeAttribute('aria-disabled');
    } else {
      this.addButton.setAttribute('aria-disabled', 'true');
    }
  };

  /* ---- Add to cart ----------------------------------------------------- */

  // Shared POST to /cart/add.js for the current variant + quantity.
  // Resolves with the cart-add response, rejects on a 4xx / soft-422.
  StickyAtcMobile.prototype.postAdd = function () {
    var v = this.currentVariant;
    var addUrl = (window.theme && window.theme.routes && window.theme.routes.cart && window.theme.routes.cart.add) || '/cart/add';
    if (!/\.js(\?|$)/.test(addUrl)) addUrl += '.js';

    return fetch(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: [(function (item) {
        // CELEBRATEVITAMINS CUSTOM — carry the subscription widget's selling plan
        var sp = window.CelebrateSubscription && window.CelebrateSubscription.sellingPlanFor(v.id);
        if (sp) item.selling_plan = sp;
        return item;
      })({ id: v.id, quantity: this.quantity })] })
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (data) {
        return { ok: res.ok, status: res.status, data: data };
      });
    }).then(function (r) {
      var soft422 = r.data && typeof r.data.status === 'number' && r.data.status >= 400;
      if (!r.ok || soft422) {
        throw new Error((r.data && (r.data.description || r.data.message)) || 'add_error');
      }
      return r.data;
    });
  };

  StickyAtcMobile.prototype.onAdd = function () {
    if (this.inFlight) return; // in-flight guard: double-tap can't fire two adds

    // First tap opens the drawer (flavour / packaging / qty) instead of adding
    // whatever is currently selected on the PDP (often off-screen). Second tap adds.
    if (!this.isExpanded()) {
      this.setExpanded(true);
      return;
    }

    var v = this.currentVariant;
    if (!v || !v.available) return;

    this.inFlight = true;
    this.clearTransientTimers();
    this.addButton.classList.remove('is-added', 'is-error');
    this.addButton.classList.add('loading'); // theme's own spinner styling
    this.addButton.setAttribute('aria-busy', 'true');
    this.addButton.disabled = true;

    var self = this;
    this.postAdd()
      .then(function (data) { self.onAddSuccess(data); })
      .catch(function (err) { self.onAddError(err); });
  };

  // Localized checkout URL (Markets-aware), e.g. "/en-de/checkout" or "/checkout".
  StickyAtcMobile.prototype.checkoutUrl = function () {
    var root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
    return root.replace(/\/+$/, '') + '/checkout';
  };

  StickyAtcMobile.prototype.onCheckout = function () {
    if (this.inFlight) return; // shares the guard with add: never both at once
    var v = this.currentVariant;
    if (!v || !v.available) return;

    this.inFlight = true;
    this.clearTransientTimers();
    this.checkoutButton.classList.remove('is-error');
    this.checkoutButton.classList.add('loading', 'is-busy');
    this.checkoutButton.setAttribute('aria-busy', 'true');

    var self = this;
    this.postAdd().then(function () {
      self.pushAddToCartAnalytics();
      // Leave the loading state visible; we're navigating away to checkout.
      window.location.href = self.checkoutUrl();
    }).catch(function (err) {
      self.inFlight = false;
      self.checkoutButton.classList.remove('loading', 'is-busy');
      self.checkoutButton.removeAttribute('aria-busy');
      self.checkoutButton.classList.add('is-error');
      if (window.console && console.warn) {
        console.warn('[sticky-atc] checkout add failed:', err && err.message ? err.message : err);
      }
      self.timers.reset = setTimeout(function () {
        self.checkoutButton.classList.remove('is-error');
        self.updateAtcState();
      }, ERROR_MS);
    });
  };

  StickyAtcMobile.prototype.onAddSuccess = function (data) {
    var self = this;
    this.addButton.classList.remove('loading');
    this.addButton.removeAttribute('aria-busy');
    this.addButton.disabled = false;
    this.inFlight = false;

    var added = this.addButton.getAttribute('data-lang-added');
    if (added && this.addText) this.addText.textContent = added;
    this.addButton.classList.add('is-added');

    this.pushAddToCartAnalytics();

    // Tell the theme a product was added (refreshes drawer contents + header count).
    try {
      document.dispatchEvent(new CustomEvent('apps:product-added-to-cart', { detail: { item: data } }));
    } catch (e) { /* no-op */ }

    // Open the existing drawer via the theme's own trigger, after its refresh delay.
    this.timers.open = setTimeout(function () { self.openCart(); }, OPEN_DELAY_MS);
    // Success is a 2s transient, then back to idle.
    this.timers.reset = setTimeout(function () { self.resetAtc(); }, SUCCESS_MS);
  };

  StickyAtcMobile.prototype.onAddError = function (err) {
    var self = this;
    this.addButton.classList.remove('loading');
    this.addButton.removeAttribute('aria-busy');
    this.addButton.disabled = false;
    this.inFlight = false;

    var errLabel = this.addButton.getAttribute('data-lang-error');
    if (errLabel && this.addText) this.addText.textContent = errLabel;
    this.addButton.classList.add('is-error');
    if (window.console && console.warn) {
      console.warn('[sticky-atc] add to cart failed:', err && err.message ? err.message : err);
    }
    this.timers.reset = setTimeout(function () { self.resetAtc(); }, ERROR_MS);
  };

  StickyAtcMobile.prototype.resetAtc = function () {
    this.clearTransientTimers();
    this.addButton.classList.remove('is-added', 'is-error');
    this.updateAtcState();
  };

  StickyAtcMobile.prototype.clearTransientTimers = function () {
    if (this.timers.reset) { clearTimeout(this.timers.reset); this.timers.reset = null; }
    if (this.timers.open) { clearTimeout(this.timers.open); this.timers.open = null; }
  };

  // Standard GA4 add_to_cart. No-op unless a container has created window.dataLayer.
  StickyAtcMobile.prototype.pushAddToCartAnalytics = function () {
    if (!window.dataLayer || typeof window.dataLayer.push !== 'function') return;
    var v = this.currentVariant;
    if (!v) return;
    var price = (typeof this.effectiveUnit === 'number' ? this.effectiveUnit : (typeof v.price === 'number' ? v.price : 0)) / 100;
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || undefined;
    var name = this.titleEl ? this.titleEl.textContent.trim() : undefined;
    try {
      window.dataLayer.push({ ecommerce: null }); // GA4-recommended reset
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: currency,
          value: price * this.quantity,
          items: [{
            item_id: String(v.id),
            item_name: name,
            price: price,
            quantity: this.quantity
          }]
        }
      });
    } catch (e) { /* no-op */ }
  };

  StickyAtcMobile.prototype.openCart = function () {
    // Guard: the theme's cart-icon handler (theme.js:8214) toggles. Never click
    // while an overlay the user opened is already open, or we'd close it.
    if (document.body.getAttribute(OVERLAY_ATTR) === 'true') return;

    // Every [data-js-cart-icon] is bound per-node (theme.js:8214), so any is valid —
    // prefer the one actually visible on the mobile header. Synthetic click is honoured.
    var icons = toArray(document.querySelectorAll('[data-js-cart-icon]'));
    if (!icons.length) return; // fail soft: keep success state, no throw, no navigation

    var target = icons.filter(function (el) {
      return el.offsetParent !== null || el.getClientRects().length > 0;
    })[0] || icons[0];

    target.click();
  };

  /* ---- Visibility ------------------------------------------------------ */

  StickyAtcMobile.prototype.setupVisibility = function () {
    var self = this;
    this.mq = window.matchMedia(MOBILE_MQ);
    this.mqActive = this.mq.matches;
    this.buyButtons = document.querySelector(BUY_BUTTONS_SELECTOR);

    if (this.buyButtons && 'IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        self.buyButtonsVisible = entries[0].isIntersecting;
        self.render();
      });
      this.io.observe(this.buyButtons);
    } else {
      // Can't observe the main button — treat it as out of view so the bar is usable.
      this.buyButtonsVisible = false;
    }

    // Hide the bar as the footer comes into view, so it never covers footer content.
    // (NB: this store has no <footer> tag, so we match .footer / [data-section-type="footer"].)
    var footer = document.querySelector(FOOTER_SELECTOR);
    if (footer && 'IntersectionObserver' in window) {
      this.footerIo = new IntersectionObserver(function (entries) {
        self.footerVisible = entries[0].isIntersecting;
        self.render();
      }, { threshold: 0 });
      this.footerIo.observe(footer);
    }

    // React to the theme's own overlay state (drawer/modal/menu open). The mobile
    // nav drawer sets this too (theme.js:7798), so the bar hides under it.
    this.bodyObserver = new MutationObserver(function () {
      self.overlayOpen = document.body.getAttribute(OVERLAY_ATTR) === 'true';
      self.render();
    });
    this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: [OVERLAY_ATTR] });

    this._onResize = function () {
      var wasMobile = self.mqActive;
      self.mqActive = self.mq.matches;
      // Leaving mobile: hand --sticky-atc-bar-height back so the native desktop bar owns it.
      if (wasMobile && !self.mqActive) {
        document.documentElement.style.setProperty(HEIGHT_VAR, '0px');
      }
      self.render();
      if (self.visible) self.setHeightVar();
    };
    window.addEventListener('resize', this._onResize);

    this.render();
  };

  StickyAtcMobile.prototype.render = function () {
    var want = this.mqActive && !this.buyButtonsVisible && !this.overlayOpen && !this.footerVisible;
    this.setVisible(want);
  };

  StickyAtcMobile.prototype.setVisible = function (visible) {
    if (visible === this.visible) return;
    this.visible = visible;
    if (visible) {
      this.root.classList.add('is-visible');
      document.documentElement.classList.add('is-sticky-atc-visible');
      this.setHeightVar();
    } else {
      this.root.classList.remove('is-visible', 'is-expanded');
      document.documentElement.classList.remove('is-sticky-atc-visible');
      if (this.expandToggle) this.expandToggle.setAttribute('aria-expanded', 'false');
      if (this.panel) this.panel.setAttribute('aria-hidden', 'true');
      this.clearHeightVar();
    }
  };

  // Reserve widget offset equal to the COLLAPSED bar height. Mobile only: on desktop the
  // native bar owns this variable. The rAF re-assert wins the race against the native bar.
  StickyAtcMobile.prototype.setHeightVar = function () {
    if (!this.mqActive) return;
    var self = this;
    var write = function () {
      var h = self.bar ? self.bar.offsetHeight : 0;
      document.documentElement.style.setProperty(HEIGHT_VAR, h + 'px');
    };
    write();
    if (window.requestAnimationFrame) window.requestAnimationFrame(write);
  };

  StickyAtcMobile.prototype.clearHeightVar = function () {
    if (!this.mqActive) return; // don't clobber the native desktop bar's value
    document.documentElement.style.setProperty(HEIGHT_VAR, '0px');
  };

  /* ---- Boot ------------------------------------------------------------ */

  function init() {
    toArray(document.querySelectorAll(BAR_SELECTOR)).forEach(function (root) {
      if (root.__stickyAtcMobileInit) return;
      root.__stickyAtcMobileInit = true;
      try {
        new StickyAtcMobile(root);
      } catch (e) {
        if (window.console && console.warn) console.warn('[sticky-atc] init failed:', e);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
